import { Form } from 'antd';
import { Modal, useMessage } from 'components/ui';
import { ModalContent } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/ModalContent';
import { STEPS } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/ModalContent/common';
import { ImportResult } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/types';
import { useEffect, useState } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router';
import { useGetIngestRecordEventsLazyQuery } from 'types/atlas-graphql';
import { ErrorState } from './ErrorState';
import pluralize from 'pluralize';
import { useUploadContext } from 'components/FileUploader';

type MultiInspectionDamageIngestionProps = {
  isOpen: boolean;
  setModalOpen: (open: boolean) => void;
};

export const MultiInspectionDamageIngestion: React.FunctionComponent<
  MultiInspectionDamageIngestionProps
> = ({ isOpen, setModalOpen }) => {
  const message = useMessage();
  const [step, setStep] = useState<number>(STEPS.upload);
  const [importResult, setImportResult] = useState<ImportResult | undefined>();
  const { setUploading } = useUploadContext();
  const [form] = Form.useForm();

  const { search, pathname } = useLocation();
  const queryParams = queryString.parse(search);
  const history = useHistory();

  const [getIngestRecordEvents, { stopPolling }] = useGetIngestRecordEventsLazyQuery({
    pollInterval: 5000,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const { getRecordEvents } = data;
      if (getRecordEvents.status && ['Complete', 'Error'].includes(getRecordEvents.status)) {
        const recordEvent =
          getRecordEvents.events?.find(event =>
            ['etl-success', 'etl-failure'].includes(event?.dataType ?? '')
          )?.event ?? {};

        if (recordEvent) {
          if (recordEvent.results && recordEvent.params.recordType === 'multi-inspection-damage') {
            const { createdDamageCount, duplicateDamageCount, invalidDamageCount } =
              recordEvent.results;
            if (createdDamageCount && !duplicateDamageCount && !invalidDamageCount) {
              message.success(`Created ${pluralize('damage', createdDamageCount, true)}`);
              handleClose();
            } else {
              setImportResult(recordEvent.results);
              setStep(STEPS.error);
              setModalOpen(true);
            }
          } else {
            setImportResult({
              errorMessage:
                'Unexpected error occurred while processing request, please try again or contact support.',
              ...recordEvent.results,
            });
            setStep(STEPS.error);
            setModalOpen(true);
          }
          if (stopPolling) stopPolling();
          setUploading(false);
        }
      }
    },
  });

  useEffect(() => {
    if (queryParams.multiInspectionUploadRecordId && queryParams.type !== 'SUCCESS') {
      handleCheckStatus(queryParams.multiInspectionUploadRecordId as string);
    }
  }, [queryParams.multiInspectionUploadRecordId]);

  const handleCheckStatus = (recordId: string) => {
    if (stopPolling) {
      stopPolling();
    }
    getIngestRecordEvents({
      variables: {
        input: {
          recordId: recordId,
          bundleId: recordId,
        },
      },
    });
  };

  const handleClose = () => {
    form.resetFields();
    setModalOpen(false);
    setStep(STEPS.upload);
    history.replace(pathname);
  };

  return (
    <Modal
      title="Upload Damages"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
    >
      <ModalContent
        form={form}
        step={step}
        setStep={setStep}
        setModalOpen={setModalOpen}
        handleCheckStatus={handleCheckStatus}
        onReset={form.resetFields}
        importResult={importResult}
        multiInspection={true}
        ErrorStateComponent={ErrorState}
      />
    </Modal>
  );
};
